import React, { useState } from 'react';
import bemify from 'bemify-js';

import { EMPTY_ZAP_NAME } from '@zapier/product-editor';
import { AsyncIndicator, ToggleSwitch } from '@zapier/design-system';
import { hasDisabledSharing } from '@zapier/design-system-zap-sharing';

import Icon from 'app/common/components/Icon';
import LogoImage from 'app/common/components/LogoImage';
// eslint-disable-next-line import/default
import Popover from 'app/common/components/Popover';
import PopoverMessage from 'app/common/components/PopoverMessage';
// eslint-disable-next-line import/default
import ZapStepIcons from 'app/common/components/ZapStepIcons';

import ZapOwnerStateful from 'app/dashboard/components/ZapOwnerStateful';
import useZap from './useZap';

const bem = bemify('dashboard-zap');

type Props = {
  disabledMessage: string;
  zapId: number;
};

const daClassName = 'zap-mini dashboard not_open';

function ZapPreview({ disabledMessage, zapId }: Props) {
  const [showTooltip, toggleTooltip] = useState(false);
  const { isLoading, zap } = useZap({ id: zapId });

  if (isLoading) return <AsyncIndicator />;

  const title = zap?.title || EMPTY_ZAP_NAME;
  const isSharingZap = !hasDisabledSharing(zap?.shareVisibility);
  /* sourceUrl is set when users create a Zap from Slack Workflow Builder */
  const isSlackWorkflowBuilder =
    zap?.sourceUrl && /app.slack.com/.exec(zap?.sourceUrl);

  const toggle = (
    <ToggleSwitch
      ariaLabel="Zap On/Off Toggle"
      checked={zap?.state === 'on'}
      disabled={true}
    />
  );

  const togglePopoverMessage = showTooltip && !!disabledMessage && (
    <PopoverMessage>{disabledMessage}</PopoverMessage>
  );
  const popoverKind = {
    dark: true,
    compact: true,
    'no-wrap': true,
  };
  const togglePopover = (
    <Popover kind={popoverKind} shouldAttach={true}>
      {toggle}
      {togglePopoverMessage}
    </Popover>
  );

  const showOwner = zap?.owner?.id && zap.isShared;
  const ownerAvatar = showOwner && (
    <div className="dashboard-zap__owner">
      <ZapOwnerStateful {...zap?.owner} avatarUrl={zap?.owner?.imageUrl} />
    </div>
  );

  const steps = zap?.nodes.map(node => ({
    key: node.selectedApi || 'unknown',
    isHidden: false,
  }));

  const className = bem({
    '--preview': true,
  });

  function stopPropagation(event?: any) {
    if (event) {
      event.stopPropagation();
    }
  }

  return (
    <div className={className} role="button" tabIndex={0}>
      <div className={daClassName} data-zap-id={zap?.id} role="presentation">
        <div className="twrapper dashboard-zap__wrapper">
          <div className="dashboard-zap__logos">
            <ZapStepIcons max={3} steps={steps} />
          </div>
          <div
            aria-label={zap?.title}
            className="title dashboard-zap__title"
            role="button"
            tabIndex={0}
          >
            {zap?.isInstant ? (
              <span className="dashboard-zap__label instant-zap">instant</span>
            ) : null}
            {isSharingZap && (
              <Icon className={bem('__icon')} size={18} type="team" />
            )}
            <span className={bem('__title-text')}>{title}</span>
            {isSlackWorkflowBuilder && (
              <div className={bem('__subtitle')}>
                <LogoImage
                  className={bem('__icon')}
                  logoClass={'SlackAPI'}
                  logoSize={16}
                />
                <span>
                  <a
                    href={zap?.sourceUrl}
                    onClick={stopPropagation}
                    target="_blank"
                  >
                    Edit in Slack
                  </a>
                </span>
              </div>
            )}
          </div>
          {ownerAvatar}
          <div className="controls dashboard-zap__controls" role="presentation">
            <span
              className="dashboard-toggle"
              onMouseEnter={() => toggleTooltip(true)}
              onMouseLeave={() => toggleTooltip(false)}
              role="presentation"
            >
              {togglePopover}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ZapPreview;
